import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';

import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import Grid from '@/components/inputs/CustomGrid';
import CustomButton from '@/components/inputs/CustomButton';
import CustomTextField from '@/components/inputs/CustomTextField';
import CustomSelect from '@/components/inputs/CustomSelect';
import useLoading from '@/components/loading/hooks/useLoading';
import useSnackbar from '@/components/dialogs/snackbars/hooks/useSnackbar';
import Connections, { ApiEndpoints } from '@/components/connections/Connections';

const iconBtn = 'inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer';

const makeLineItem = vatRate => ({
  description: '',
  code: '',
  quantity: 1,
  unit: '',
  unitPrice: 0,
  discountPercentage: 0,
  vatRate,
  isService: false,
});

const invoiceTypeOptions = [
  { key: 'invoice', value: 'invoice', text: 'invoice.type.invoice' },
  { key: 'proforma', value: 'proforma', text: 'invoice.type.proforma' },
  { key: 'credit_note', value: 'credit_note', text: 'invoice.type.credit_note' },
];

const paymentMethodOptions = [
  { key: 'bank_transfer', value: 'bank_transfer', text: 'invoice.payment.bank_transfer' },
  { key: 'cash', value: 'cash', text: 'invoice.payment.cash' },
  { key: 'card', value: 'card', text: 'invoice.payment.card' },
];

const calculateItem = item => {
  const gross = item.quantity * item.unitPrice;
  const discount = gross * (item.discountPercentage / 100);
  const totalNet = Math.round((gross - discount) * 100) / 100;
  const vatAmount = Math.round(totalNet * (item.vatRate / 100) * 100) / 100;
  const totalGross = Math.round((totalNet + vatAmount) * 100) / 100;
  return { ...item, totalNet, vatAmount, totalGross };
};

const SectionTitle = ({ children, className = '' }) => (
  <h2 className={`text-base font-semibold ${className}`}>{children}</h2>
);

const InvoiceForm = () => {
  const { companyId, invoiceId } = useParams();
  const isEdit = !!invoiceId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, Loading, setLoading } = useLoading(isEdit);
  const { createSuccessSnackbar, createErrorSnackbar } = useSnackbar();

  const [customers, setCustomers] = useState([]);
  const [useExistingCustomer, setUseExistingCustomer] = useState(true);
  const [values, setValues] = useState({
    customerId: '',
    type: 'invoice',
    language: 'bg',
    issueDate: new Date().toISOString().split('T')[0],
    taxEventDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    dealType: '',
    correspondingDocument: '',
    paymentMethod: 'bank_transfer',
    currency: 'EUR',
    customContent: '',
    notes: '',
  });
  const [adhocCustomer, setAdhocCustomer] = useState({
    name: '', eik: '', bulstat: '', vatId: '', accountablePerson: '',
    address: { street: '', city: '', zip: '', country: '' },
    email: '',
  });
  const [defaultVat, setDefaultVat] = useState(20);
  const [vatRegistered, setVatRegistered] = useState(true);
  const [items, setItems] = useState([makeLineItem(20)]);

  useEffect(() => {
    const load = async () => {
      const [custRes, compRes] = await Promise.all([
        Connections.postRequest(ApiEndpoints.customersAll, { companyId }),
        Connections.postRequest(ApiEndpoints.companyGet, { companyId }),
      ]);
      if (custRes.ok) setCustomers(custRes.data.customers || []);

      const companyVatSettings = compRes.ok ? compRes.data.company?.settings?.vat : null;
      const isVatRegistered = companyVatSettings?.vatRegistered !== false
        && companyVatSettings?.art97a !== true;
      setVatRegistered(isVatRegistered);
      const vat = isVatRegistered ? (Number(companyVatSettings?.defaultVat ?? 20) || 0) : 0;
      setDefaultVat(vat);
      if (!isEdit) setItems([makeLineItem(vat)]);

      if (isEdit) {
        const invRes = await Connections.postRequest(ApiEndpoints.invoicesAll, { companyId });
        if (invRes.ok) {
          const inv = (invRes.data.invoices || []).find(i => i._id === invoiceId);
          if (inv) {
            setValues({
              customerId: inv.customerId || '',
              type: inv.type || 'invoice',
              language: inv.language || 'bg',
              issueDate: inv.issueDate ? inv.issueDate.split('T')[0] : '',
              taxEventDate: inv.taxEventDate ? inv.taxEventDate.split('T')[0] : '',
              dueDate: inv.dueDate ? inv.dueDate.split('T')[0] : '',
              dealType: inv.dealType || '',
              correspondingDocument: inv.correspondingDocument || '',
              paymentMethod: inv.paymentMethod || 'bank_transfer',
              currency: inv.currency || 'EUR',
              customContent: inv.customContent || '',
              notes: inv.notes || '',
            });
            if (inv.items?.length) {
              setItems(isVatRegistered ? inv.items : inv.items.map(i => ({ ...i, vatRate: 0 })));
            }
            if (!inv.customerId && inv.customerSnapshot?.name) {
              setUseExistingCustomer(false);
              setAdhocCustomer(inv.customerSnapshot);
            }
          }
        }
      }
      setLoading(false);
    };
    load().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, invoiceId]);

  const handleChange = ({ name, value }) => {
    setValues(prev => ({ ...prev, [name]: value }));
  };

  const handleAdhocChange = ({ name, value }) => {
    setAdhocCustomer(prev => ({ ...prev, [name]: value }));
  };

  const handleAdhocAddressChange = ({ name, value }) => {
    setAdhocCustomer(prev => ({ ...prev, address: { ...prev.address, [name]: value } }));
  };

  const handleItemChange = (index, field, value) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addItem = () => setItems(prev => [...prev, makeLineItem(defaultVat)]);

  const removeItem = index => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculatedItems = items.map(calculateItem);
  const subtotalNet = calculatedItems.reduce((sum, i) => sum + i.totalNet, 0);
  const totalVat = calculatedItems.reduce((sum, i) => sum + i.vatAmount, 0);
  const totalGross = calculatedItems.reduce((sum, i) => sum + i.totalGross, 0);

  const handleSave = async () => {
    if (!calculatedItems.length) return;

    setLoading(true);
    const endpoint = isEdit ? ApiEndpoints.invoiceUpdate : ApiEndpoints.invoiceCreate;

    const params = {
      companyId,
      ...(isEdit && { invoiceId }),
      ...values,
      items: calculatedItems,
      ...(useExistingCustomer
        ? { customerId: values.customerId || null }
        : { customerId: null, customerSnapshot: adhocCustomer }),
    };

    const res = await Connections.postRequest(endpoint, params);
    if (res.ok) {
      createSuccessSnackbar(t(isEdit ? 'invoice.updated' : 'invoice.created'));
      navigate(`/companies/${companyId}?tab=invoices`);
    } else {
      createErrorSnackbar(res.errorMessage);
    }
    setLoading(false);
  };

  if (loading) return <Loading />;

  const customerOptions = customers.map(c => ({ key: c._id, value: c._id, text: c.name }));

  const languageOptions = [
    { key: 'bg', value: 'bg', text: 'BG' },
    { key: 'en', value: 'en', text: 'EN' },
    { key: 'de', value: 'de', text: 'DE' },
  ];

  const cellInput = 'w-full bg-card border border-input rounded-md px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <>
      <Helmet>
        <title>{t('app.title')} | {t(isEdit ? 'invoice.edit' : 'invoice.create')}</title>
      </Helmet>
      <h1 className="text-2xl font-semibold mb-6">
        {t(isEdit ? 'invoice.edit' : 'invoice.create')}
      </h1>

      {/* Invoice Details */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <CustomSelect name="type" label={t('invoice.type')} value={values.type} onChange={handleChange} options={invoiceTypeOptions} fullWidth />
        </Grid>
        <Grid item xs={12} md={2}>
          <CustomSelect name="language" label={t('language')} value={values.language} onChange={handleChange} options={languageOptions} fullWidth />
        </Grid>
        <Grid item xs={12} md={2}>
          <CustomTextField fieldName="currency" label={t('currency')} value={values.currency} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomSelect name="paymentMethod" label={t('paymentMethod')} value={values.paymentMethod} onChange={handleChange} options={paymentMethodOptions} fullWidth />
        </Grid>
      </Grid>

      <Grid container spacing={2} className="mt-2">
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="issueDate" label={t('invoice.issueDate')} value={values.issueDate} onChange={handleChange} fullWidth type="date" />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="taxEventDate" label={t('invoice.taxEventDate')} value={values.taxEventDate} onChange={handleChange} fullWidth type="date" />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="dueDate" label={t('dueDate')} value={values.dueDate} onChange={handleChange} fullWidth type="date" />
        </Grid>
      </Grid>

      {/* Customer */}
      <Separator className="my-6" />
      <div className="flex items-center gap-4 mb-3">
        <SectionTitle>{t('customer')}</SectionTitle>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <Checkbox
            checked={useExistingCustomer}
            onCheckedChange={checked => setUseExistingCustomer(checked === true)}
          />
          {t('invoice.useExistingCustomer')}
        </label>
      </div>

      {useExistingCustomer ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CustomSelect
              name="customerId" label={t('customer.select')} value={values.customerId}
              onChange={handleChange}
              options={[{ key: '', value: '', text: '---' }, ...customerOptions]}
              fullWidth
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <CustomTextField fieldName="name" label={t('name')} value={adhocCustomer.name} onChange={handleAdhocChange} fullWidth />
          </Grid>
          <Grid item xs={12} md={2}>
            <CustomTextField fieldName="eik" label={t('eik')} value={adhocCustomer.eik} onChange={handleAdhocChange} fullWidth />
          </Grid>
          <Grid item xs={12} md={2}>
            <CustomTextField fieldName="vatId" label={t('vatId')} value={adhocCustomer.vatId} onChange={handleAdhocChange} fullWidth />
          </Grid>
          <Grid item xs={12} md={2}>
            <CustomTextField fieldName="accountablePerson" label={t('accountablePerson')} value={adhocCustomer.accountablePerson} onChange={handleAdhocChange} fullWidth />
          </Grid>
          <Grid item xs={12} md={3}>
            <CustomTextField fieldName="email" label={t('email')} value={adhocCustomer.email} onChange={handleAdhocChange} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomTextField fieldName="street" label={t('address.street')} value={adhocCustomer.address.street} onChange={handleAdhocAddressChange} fullWidth />
          </Grid>
          <Grid item xs={12} md={2}>
            <CustomTextField fieldName="city" label={t('address.city')} value={adhocCustomer.address.city} onChange={handleAdhocAddressChange} fullWidth />
          </Grid>
          <Grid item xs={12} md={2}>
            <CustomTextField fieldName="country" label={t('address.country')} value={adhocCustomer.address.country} onChange={handleAdhocAddressChange} fullWidth />
          </Grid>
        </Grid>
      )}

      {/* Line Items */}
      <Separator className="my-6" />
      <div className="flex justify-between items-center mb-3">
        <SectionTitle>{t('invoice.items')}</SectionTitle>
        <button type="button" className={`${iconBtn} text-primary`} onClick={addItem}>
          <PlusIcon size={20} />
        </button>
      </div>
      <Card className="overflow-x-auto mb-4">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium min-w-[200px]">{t('description')}</th>
              <th className="px-3 py-2 font-medium w-20">{t('qty')}</th>
              <th className="px-3 py-2 font-medium w-20">{t('unit')}</th>
              <th className="px-3 py-2 font-medium w-24">{t('price')}</th>
              <th className="px-3 py-2 font-medium w-20">{t('vat')} %</th>
              <th className="px-3 py-2 font-medium w-20">{t('discount')} %</th>
              <th className="px-3 py-2 font-medium text-right w-24">{t('net')}</th>
              <th className="px-3 py-2 font-medium text-right w-24">{t('gross')}</th>
              <th className="px-3 py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {calculatedItems.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={index} className="border-t">
                <td className="px-3 py-2">
                  <input
                    className={cellInput} value={item.description}
                    onChange={e => handleItemChange(index, 'description', e.target.value)}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number" className={cellInput} value={item.quantity}
                    onChange={e => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    className={cellInput} value={item.unit}
                    onChange={e => handleItemChange(index, 'unit', e.target.value)}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number" className={cellInput} value={item.unitPrice}
                    onChange={e => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number" className={cellInput} value={item.vatRate}
                    disabled={!vatRegistered}
                    onChange={e => handleItemChange(index, 'vatRate', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number" className={cellInput} value={item.discountPercentage}
                    onChange={e => handleItemChange(index, 'discountPercentage', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td className="px-3 py-2 text-right">{item.totalNet.toFixed(2)}</td>
                <td className="px-3 py-2 text-right">{item.totalGross.toFixed(2)}</td>
                <td className="px-3 py-2">
                  {items.length > 1 && (
                    <button
                      type="button"
                      className={`${iconBtn} hover:text-destructive`}
                      onClick={() => removeItem(index)}
                    >
                      <TrashIcon size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Totals */}
      <div className="flex justify-end">
        <div className="min-w-[260px]">
          <div className="flex justify-between mb-1">
            <span className="text-sm text-muted-foreground">{t('subtotalNet')}:</span>
            <span className="text-sm">{subtotalNet.toFixed(2)} {values.currency}</span>
          </div>
          <div className="flex justify-between mb-1">
            <span className="text-sm text-muted-foreground">{t('totalVat')}:</span>
            <span className="text-sm">
              {vatRegistered ? `${totalVat.toFixed(2)} ${values.currency}` : t('vat.notCharged')}
            </span>
          </div>
          <Separator className="my-2" />
          <div className="flex justify-between">
            <span className="text-base font-semibold">{t('totalGross')}:</span>
            <span className="text-base font-semibold">
              {totalGross.toFixed(2)} {values.currency}
            </span>
          </div>
        </div>
      </div>

      {/* Additional Fields */}
      <Separator className="my-6" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <CustomTextField fieldName="dealType" label={t('dealType')} value={values.dealType} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField
            fieldName="correspondingDocument" label={t('correspondingDocument')}
            value={values.correspondingDocument} onChange={handleChange} fullWidth
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} className="mt-2">
        <Grid item xs={12}>
          <CustomTextField
            fieldName="customContent" label={t('invoice.customContent')}
            value={values.customContent} onChange={handleChange} fullWidth multiline rows={3}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextField
            fieldName="notes" label={t('notes')} value={values.notes}
            onChange={handleChange} fullWidth multiline rows={2}
          />
        </Grid>
      </Grid>

      <div className="flex justify-end mt-8 gap-3">
        <CustomButton variant="outlined" onClick={() => navigate(`/companies/${companyId}?tab=invoices`)}>
          {t('cancel')}
        </CustomButton>
        <CustomButton onClick={handleSave}>
          {t(isEdit ? 'save' : 'create')}
        </CustomButton>
      </div>
    </>
  );
};

export default InvoiceForm;
